import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, BookOpen, Users, UserCheck, Stethoscope, Building2, TrendingUp, ChevronDown, ChevronRight, Shield, Target, CheckCircle, AlertCircle, Clock, Star, Heart, Eye, FileText, DollarSign, Calendar, MessageSquare, ArrowLeft } from 'lucide-react';

const Manualistica = () => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('percorso');
  const [expandedRole, setExpandedRole] = useState(null);

  // Check if user is admin or doctor
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user.role === 'admin' || user.role === 'doctor') {
        setIsAuthorized(true);
      }
    }
    setLoading(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #E8E4DC 100%)'}}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{borderColor: '#2A9FCB', borderTopColor: 'transparent'}}></div>
          <p style={{color: '#2A9FCB'}}>Caricamento...</p>
        </div>
      </div>
    );
  }

  // Not authorized screen
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #E8E4DC 100%)'}}>
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full mx-4 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'}}>
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-red-600 mb-2">Accesso Negato</h1>
          <p className="text-gray-600 mb-6">
            Questa sezione è riservata al personale medico e amministrativo di PRIME VISION.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium"
            style={{background: '#2A9FCB'}}
          >
            <ArrowLeft className="w-5 h-5" />
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  // Navigation tabs
  const tabs = [
    { id: 'percorso', label: 'Percorso Paziente', icon: Users },
    { id: 'esperienza', label: 'Esperienza 5 Stelle', icon: Star },
    { id: 'sito', label: 'Area Riservata Web', icon: Eye },
    { id: 'abbonamenti', label: 'Abbonamenti', icon: DollarSign },
    { id: 'mansionari', label: 'Mansionari', icon: FileText },
  ];

  // Role data for mansionari
  const roles = [
    {
      id: 'medico',
      title: 'Medico Oculista',
      icon: Stethoscope,
      color: '#2A9FCB',
      reportTo: 'Chiara Rossi (Direttore Generale)',
      description: 'Responsabile della diagnosi, cura e trattamento delle patologie oculari dei pazienti.',
      responsibilities: [
        {
          category: 'Attività Clinica',
          tasks: [
            'Eseguire visite oculistiche complete (anamnesi, esame obiettivo, diagnosi)',
            'Compilare la cartella clinica elettronica con precisione e completezza',
            'Prescrivere terapie farmacologiche e/o correttive (occhiali, LAC)',
            'Eseguire procedure diagnostiche strumentali (OCT, campo visivo, topografia)',
            'Gestire urgenze oftalmologiche',
            'Seguire i follow-up dei pazienti cronici (glaucoma, AMD, diabetici)'
          ]
        },
        {
          category: 'Gestione Pazienti',
          tasks: [
            'Comunicare diagnosi e prognosi in modo chiaro e comprensibile',
            'Rispondere alle domande dei pazienti con pazienza e professionalità',
            'Gestire le aspettative dei pazienti in modo realistico',
            'Segnalare casi complessi per consulto multidisciplinare'
          ]
        },
        {
          category: 'Amministrativo',
          tasks: [
            'Gestire la propria disponibilità nel sistema di prenotazione',
            'Rispondere ai messaggi interni entro 24h',
            'Partecipare alle riunioni di staff mensili',
            'Aggiornarsi sulle linee guida nazionali e internazionali'
          ]
        }
      ],
      kpis: [
        'Numero visite/giorno: Target 15-20',
        'Tempo medio visita: 20-30 minuti',
        'Tasso di soddisfazione pazienti: >90%',
        'Completezza cartelle cliniche: 100%',
        'Risposta messaggi: <24h'
      ],
      expectedValue: 'Diagnosi accurate, pazienti soddisfatti, cartelle complete, comunicazione efficace con lo staff.',
      earnings: '€60 per visita base. Bonus per procedure speciali.'
    },
    {
      id: 'ortottista',
      title: 'Ortottista',
      icon: Eye,
      color: '#2A9FCB',
      reportTo: 'Medico Oculista referente / Chiara Rossi',
      description: 'Specialista nella valutazione e riabilitazione della funzione visiva, dello strabismo e dell\'ambliopia.',
      responsibilities: [
        {
          category: 'Attività Clinica',
          tasks: [
            'Eseguire esami ortottici completi (cover test, motilità, stereopsi)',
            'Valutare e trattare ambliopia nei bambini',
            'Gestire la riabilitazione visiva pre e post operatoria',
            'Eseguire esami strumentali (biometria, pachimetria, campo visivo)',
            'Assistere il medico durante procedure diagnostiche',
            'Gestire il training ortottico per pazienti con strabismo'
          ]
        },
        {
          category: 'Gestione Pazienti Pediatrici',
          tasks: [
            'Accogliere i bambini con approccio ludico e rassicurante',
            'Spiegare ai genitori le terapie e il follow-up necessario',
            'Gestire le occlusioni e i controlli periodici',
            'Educare le famiglie sull\'importanza della compliance'
          ]
        },
        {
          category: 'Strumentazione',
          tasks: [
            'Mantenere calibrata la strumentazione diagnostica',
            'Segnalare malfunzionamenti al responsabile',
            'Preparare i report strumentali per il medico'
          ]
        }
      ],
      kpis: [
        'Esami ortottici/giorno: Target 20-25',
        'Tempo medio esame: 15-20 minuti',
        'Compliance pazienti pediatrici: >85%',
        'Report completati entro fine giornata: 100%'
      ],
      expectedValue: 'Esami accurati, pazienti pediatrici seguiti con cura, report tempestivi.',
      earnings: 'Stipendio fisso + incentivi su volume.'
    },
    {
      id: 'admin',
      title: 'Admin / Segreteria',
      icon: Building2,
      color: '#F59E0B',
      reportTo: 'Chiara Rossi (Direttore Generale)',
      description: 'Primo punto di contatto con i pazienti. Gestisce prenotazioni, accoglienza, pagamenti e comunicazioni.',
      responsibilities: [
        {
          category: 'Accoglienza',
          tasks: [
            'Accogliere i pazienti con sorriso e professionalità',
            'Verificare identità e documenti del paziente',
            'Registrare l\'arrivo nel sistema (check-in)',
            'Gestire la sala d\'attesa (comfort, tempi, comunicazione ritardi)',
            'Rispondere al telefono entro 3 squilli',
            'Chiamare i pazienti per le visite quando richiesto dal medico'
          ]
        },
        {
          category: 'Prenotazioni',
          tasks: [
            'Gestire le prenotazioni telefoniche e online',
            'Ottimizzare l\'agenda dei medici',
            'Inviare promemoria 24-48h prima dell\'appuntamento',
            'Gestire cancellazioni e riprogrammazioni',
            'Verificare la conferma dei pazienti ai promemoria'
          ]
        },
        {
          category: 'Amministrazione',
          tasks: [
            'Incassare pagamenti (contanti, POS, fatture)',
            'Emettere ricevute e fatture',
            'Gestire gli abbonamenti dei pazienti',
            'Archiviare documentazione',
            'Monitorare le statistiche giornaliere'
          ]
        },
        {
          category: 'Comunicazione',
          tasks: [
            'Rispondere ai messaggi interni dei medici',
            'Gestire le comunicazioni con i pazienti (WhatsApp, email)',
            'Segnalare problematiche a Chiara Rossi',
            'Coordinare con ortottisti e medici per ottimizzare i flussi'
          ]
        }
      ],
      kpis: [
        'Tempo medio attesa pazienti: <15 minuti',
        'Tasso di risposta telefonica: >95%',
        'Errori di prenotazione: <1%',
        'Soddisfazione pazienti accoglienza: >95%',
        'Incassi registrati correttamente: 100%'
      ],
      expectedValue: 'Pazienti accolti con calore, agenda ottimizzata, zero errori amministrativi, comunicazione fluida.',
      earnings: 'Stipendio fisso.'
    },
    {
      id: 'chiara',
      title: 'Chiara Rossi',
      subtitle: 'Produzione, Ufficio Acquisti, Direttore Generale',
      icon: Shield,
      color: '#8B5CF6',
      reportTo: 'Proprietà / Soci',
      description: 'Responsabile della direzione generale, della produzione e degli acquisti. Supervisiona tutte le operazioni cliniche e amministrative.',
      responsibilities: [
        {
          category: 'Direzione Generale',
          tasks: [
            'Definire e monitorare gli obiettivi aziendali',
            'Supervisionare il lavoro di medici, ortottisti e admin',
            'Gestire le problematiche operative quotidiane',
            'Garantire il rispetto degli standard di qualità',
            'Condurre riunioni settimanali con lo staff',
            'Reportare alla proprietà su base mensile'
          ]
        },
        {
          category: 'Produzione',
          tasks: [
            'Pianificare i turni del personale',
            'Ottimizzare i flussi di lavoro per massimizzare l\'efficienza',
            'Gestire le emergenze operative',
            'Implementare miglioramenti ai processi',
            'Monitorare i KPI di produttività'
          ]
        },
        {
          category: 'Ufficio Acquisti',
          tasks: [
            'Gestire gli ordini di materiale sanitario',
            'Negoziare con i fornitori per ottenere condizioni vantaggiose',
            'Controllare le scorte e prevenire esaurimenti',
            'Verificare la qualità dei prodotti acquistati',
            'Gestire il budget acquisti'
          ]
        },
        {
          category: 'Risorse Umane (supporto)',
          tasks: [
            'Partecipare alla selezione del personale',
            'Gestire i conflitti interni',
            'Motivare e supportare il team',
            'Identificare necessità formative'
          ]
        }
      ],
      kpis: [
        'Fatturato mensile: Target definito annualmente',
        'Margine operativo: >40%',
        'Turnover personale: <10% annuo',
        'Soddisfazione staff: >85%',
        'Tempi di approvvigionamento: <48h per urgenze'
      ],
      expectedValue: 'Clinica efficiente, staff motivato, qualità costante, obiettivi raggiunti.',
      earnings: 'Stipendio + bonus su obiettivi.'
    },
    {
      id: 'ruggiero',
      title: 'Ruggiero di Luggo',
      subtitle: 'Marketing, Amministrazione, Vendite, HR',
      icon: TrendingUp,
      color: '#EF4444',
      reportTo: 'Proprietà / Soci',
      description: 'Responsabile delle attività commerciali, marketing, amministrazione e gestione delle risorse umane.',
      responsibilities: [
        {
          category: 'Marketing',
          tasks: [
            'Definire e implementare la strategia di marketing',
            'Gestire i social media e la presenza online',
            'Creare contenuti promozionali (post, video, articoli)',
            'Gestire le campagne pubblicitarie (Google Ads, Facebook)',
            'Monitorare la reputazione online e le recensioni',
            'Organizzare eventi e iniziative promozionali'
          ]
        },
        {
          category: 'Vendite / Commerciale',
          tasks: [
            'Sviluppare partnership con aziende e assicurazioni',
            'Promuovere gli abbonamenti ai pazienti',
            'Gestire le convenzioni aziendali',
            'Analizzare il mercato e la concorrenza',
            'Proporre nuovi servizi e offerte'
          ]
        },
        {
          category: 'Amministrazione',
          tasks: [
            'Supervisionare la contabilità',
            'Gestire i rapporti con il commercialista',
            'Controllare i flussi di cassa',
            'Preparare report finanziari mensili',
            'Gestire fatturazione e incassi'
          ]
        },
        {
          category: 'Risorse Umane',
          tasks: [
            'Gestire il recruiting e la selezione del personale',
            'Definire i contratti di lavoro',
            'Gestire buste paga e adempimenti',
            'Pianificare la formazione del personale',
            'Gestire le valutazioni periodiche',
            'Sviluppare piani di incentivazione'
          ]
        }
      ],
      kpis: [
        'Nuovi pazienti/mese: Target 50+',
        'Tasso conversione abbonamenti: >20%',
        'ROI campagne marketing: >300%',
        'Follower social crescita: +10% mensile',
        'Turnover personale: <10%',
        'Cash flow positivo: Sempre'
      ],
      expectedValue: 'Brand forte, pazienti in crescita, finanze in ordine, team stabile e motivato.',
      earnings: 'Stipendio + bonus su obiettivi commerciali.'
    }
  ];

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
      {/* Header */}
      <div className="p-6" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-white" />
            <h1 className="text-2xl font-bold text-white">Manualistica PRIME VISION</h1>
          </div>
          <p className="text-white/80">Documentazione interna e procedure operative</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === tab.id 
                    ? 'border-green-600 text-green-600' 
                    : 'border-transparent text-[#2A9FCB]/70 hover:text-[#2A9FCB]'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* PERCORSO PAZIENTE */}
        {activeSection === 'percorso' && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6" style={{color: '#2A9FCB'}}>
                <Users className="w-7 h-7 inline mr-3" />
                Il Percorso del Paziente
              </h2>
              
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">Prenotazione</h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <p><strong>Online (Sito Web):</strong></p>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-[#2A9FCB]/80 ml-4">
                        <li>Il paziente accede al sito primevision.it</li>
                        <li>Clicca su "Prenota Visita"</li>
                        <li>Sceglie se prenotare per medico specifico o per data disponibile</li>
                        <li>Seleziona il tipo di visita (Prima Visita, Controllo, Urgenza)</li>
                        <li>Sceglie data e orario tra quelli disponibili</li>
                        <li>Inserisce i propri dati (se nuovo) o fa login (se già registrato)</li>
                        <li>Verifica email con codice OTP</li>
                        <li>Verifica telefono con SMS</li>
                        <li>Conferma la prenotazione</li>
                        <li>Riceve email di conferma con dettagli appuntamento</li>
                      </ol>
                      <p className="mt-3"><strong>Telefonica:</strong></p>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-[#2A9FCB]/80 ml-4">
                        <li>Il paziente chiama il numero della clinica</li>
                        <li>L'admin risponde entro 3 squilli</li>
                        <li>Raccoglie i dati del paziente</li>
                        <li>Propone le date disponibili</li>
                        <li>Conferma l'appuntamento verbalmente</li>
                        <li>Invia SMS di conferma</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">Promemoria (24-48h prima)</h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <ul className="list-disc list-inside space-y-1 text-sm text-[#2A9FCB]/80">
                        <li>Il sistema invia automaticamente un promemoria via SMS/WhatsApp</li>
                        <li>Il paziente può confermare o disdire tramite il link nel messaggio</li>
                        <li>Se conferma: l'admin riceve notifica di conferma</li>
                        <li>Se disdice: l'admin riceve notifica e lo slot si libera</li>
                        <li><strong>Il promemoria viene inviato UNA SOLA VOLTA</strong></li>
                        <li>Non viene reinviato se il paziente ha già risposto</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">Arrivo in Clinica</h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <ul className="list-disc list-inside space-y-1 text-sm text-[#2A9FCB]/80">
                        <li>Il paziente arriva in clinica</li>
                        <li>L'admin lo accoglie con sorriso: "Buongiorno! Come posso aiutarla?"</li>
                        <li>Verifica l'identità (nome, cognome, appuntamento)</li>
                        <li>Effettua il <strong>CHECK-IN</strong> nel sistema</li>
                        <li>Verifica se è già registrato o se è prima visita</li>
                        <li>Se prima visita: fa compilare il modulo anamnesi</li>
                        <li>Gestisce il pagamento (anticipato o posticipato)</li>
                        <li>Invita il paziente ad accomodarsi in sala d'attesa</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">Sala d'Attesa</h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <ul className="list-disc list-inside space-y-1 text-sm text-[#2A9FCB]/80">
                        <li>Il paziente attende il proprio turno</li>
                        <li>La sala d'attesa è confortevole con riviste e TV</li>
                        <li>L'admin monitora i tempi di attesa</li>
                        <li>Se l'attesa supera i 15 minuti, l'admin informa il paziente</li>
                        <li>Il medico, quando pronto, seleziona il paziente nel sistema e indica la STANZA (A o B)</li>
                        <li>L'admin riceve la notifica: "Dr. Rossi chiama Mario Bianchi - STANZA A"</li>
                        <li>L'admin chiama il paziente: "Signor Bianchi, prego, stanza A"</li>
                        <li>L'admin conferma nel sistema che il paziente è stato inviato</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <span className="text-white font-bold">5</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">Visita Medica</h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <ul className="list-disc list-inside space-y-1 text-sm text-[#2A9FCB]/80">
                        <li>Il medico accoglie il paziente con stretta di mano</li>
                        <li>Chiede il motivo della visita</li>
                        <li>Raccoglie l'anamnesi se non già presente</li>
                        <li>Esegue l'esame obiettivo completo</li>
                        <li>Utilizza strumenti diagnostici necessari</li>
                        <li>Formula diagnosi e spiega in modo chiaro</li>
                        <li>Prescrive terapia e/o esami aggiuntivi</li>
                        <li>Compila la cartella clinica elettronica</li>
                        <li>Programma eventuali follow-up</li>
                        <li>Risponde alle domande del paziente</li>
                        <li>Conclude la visita e congeda il paziente</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Step 6 */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <span className="text-white font-bold">6</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-2">Congedo e Pagamento</h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <ul className="list-disc list-inside space-y-1 text-sm text-[#2A9FCB]/80">
                        <li>Il paziente torna all'accettazione</li>
                        <li>L'admin verifica se il pagamento è già stato effettuato</li>
                        <li>Se non pagato: incassa il dovuto (contanti, carta, fattura)</li>
                        <li>Emette ricevuta/fattura</li>
                        <li>Se necessario, prenota il prossimo appuntamento</li>
                        <li>Consegna eventuali prescrizioni stampate</li>
                        <li>Saluta cordialmente: "Arrivederci, buona giornata!"</li>
                        <li>Aggiorna lo stato nel sistema: "VISITA COMPLETATA"</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ESPERIENZA 5 STELLE */}
        {activeSection === 'esperienza' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6" style={{color: '#2A9FCB'}}>
                <Star className="w-7 h-7 inline mr-3" />
                Come Offrire un'Esperienza 5 Stelle
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    DA FARE (Best Practices)
                  </h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Sorridi sempre</strong> - Un sorriso genuino mette a proprio agio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Usa il nome del paziente</strong> - "Signor Rossi" crea connessione</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Ascolta attivamente</strong> - Non interrompere, annuisci</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Spiega tutto</strong> - "Ora faremo questo esame perché..."</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Rispetta i tempi</strong> - Se c'è ritardo, avvisa subito</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Ambiente impeccabile</strong> - Pulizia, ordine, comfort</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Follow-up proattivo</strong> - "Come sta? Ha domande?"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span><strong>Risolvi subito i problemi</strong> - Non rimandare mai</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    DA NON FARE (Errori da Evitare)
                  </h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Mai sembrare annoiati</strong> - Anche dopo 100 visite</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non parlare tra colleghi</strong> ignorando il paziente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non usare termini tecnici</strong> senza spiegare</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non far aspettare</strong> senza comunicare</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non essere sbrigativi</strong> - Il paziente lo percepisce</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non discutere mai</strong> con il paziente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non criticare colleghi</strong> o altre strutture</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">✗</span>
                      <span><strong>Non usare il cellulare</strong> durante la visita</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="font-bold text-lg mb-4 text-blue-700">
                  <Heart className="w-5 h-5 inline mr-2" />
                  Le Frasi Chiave
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-2">All'arrivo:</p>
                    <ul className="space-y-1 text-[#2A9FCB]/80">
                      <li>"Buongiorno! Benvenuto/a in PRIME VISION"</li>
                      <li>"Come posso aiutarla?"</li>
                      <li>"Ha prenotato per le [ora]?"</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium mb-2">Durante l'attesa:</p>
                    <ul className="space-y-1 text-[#2A9FCB]/80">
                      <li>"Si accomodi, sarà chiamato/a a breve"</li>
                      <li>"Posso offrirle dell'acqua?"</li>
                      <li>"Mi scuso per l'attesa, il dottore è ancora impegnato"</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium mb-2">Durante la visita:</p>
                    <ul className="space-y-1 text-[#2A9FCB]/80">
                      <li>"Mi parli del suo problema"</li>
                      <li>"Le spiego cosa stiamo facendo..."</li>
                      <li>"Ha domande?"</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium mb-2">Al congedo:</p>
                    <ul className="space-y-1 text-[#2A9FCB]/80">
                      <li>"È stato un piacere. A presto!"</li>
                      <li>"Se ha dubbi, non esiti a contattarci"</li>
                      <li>"Arrivederci e buona giornata!"</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AREA RISERVATA WEB */}
        {activeSection === 'sito' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6" style={{color: '#2A9FCB'}}>
                <Eye className="w-7 h-7 inline mr-3" />
                Come Funziona il Sito Web - Area Riservata
              </h2>

              <div className="space-y-8">
                <div>
                  <h3 className="font-bold text-lg mb-4">Per i Pazienti</h3>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Dashboard Paziente</h4>
                      <ul className="list-disc list-inside text-sm text-[#2A9FCB]/80 space-y-1">
                        <li><strong>Visite:</strong> Visualizza appuntamenti passati e futuri</li>
                        <li><strong>Anamnesi:</strong> Compila e aggiorna la propria anamnesi</li>
                        <li><strong>Test Visivi:</strong> Esegui test di autovalutazione (Ishihara, OSDI, ecc.)</li>
                        <li><strong>Cartelle Cliniche:</strong> Accedi ai referti delle visite</li>
                        <li><strong>Abbonamento:</strong> Visualizza e gestisci il proprio abbonamento</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-lg mb-4">Per i Medici</h3>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Dashboard Medico</h4>
                      <ul className="list-disc list-inside text-sm text-[#2A9FCB]/80 space-y-1">
                        <li><strong>Agenda:</strong> Visualizza appuntamenti del giorno e della settimana</li>
                        <li><strong>Disponibilità:</strong> Imposta giorni/orari disponibili con conferma modale</li>
                        <li><strong>Sala d'Attesa:</strong> Vedi pazienti in attesa, chiamali in stanza A o B</li>
                        <li><strong>Pazienti:</strong> Database pazienti con filtri avanzati</li>
                        <li><strong>Cartella Clinica:</strong> Compila la cartella con layout OD/OS affiancati</li>
                        <li><strong>Messaggi:</strong> Comunica con admin e colleghi via chat interna</li>
                        <li><strong>Linee Guida:</strong> Consulta le linee guida per patologia</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-lg mb-4">Per l'Admin</h3>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Dashboard Admin</h4>
                      <ul className="list-disc list-inside text-sm text-[#2A9FCB]/80 space-y-1">
                        <li><strong>Statistiche:</strong> Pazienti, visite, ricavi (giorno, mese, 3/6/12 mesi)</li>
                        <li><strong>Gestione Medici:</strong> Aggiungi/modifica medici e ortottisti</li>
                        <li><strong>Gestione Stanze:</strong> Configura stanze A, B, ecc.</li>
                        <li><strong>Pazienti Chiamati:</strong> Vedi chi è stato chiamato da quale medico in quale stanza</li>
                        <li><strong>Promemoria:</strong> Invia promemoria 24-48h ai pazienti</li>
                        <li><strong>Backup:</strong> Esporta dati del sistema</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ABBONAMENTI */}
        {activeSection === 'abbonamenti' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6" style={{color: '#2A9FCB'}}>
                <DollarSign className="w-7 h-7 inline mr-3" />
                Sistema Abbonamenti
              </h2>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* BASE */}
                <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                  <h3 className="font-bold text-xl mb-2">BASE</h3>
                  <div className="mb-4">
                    <p className="text-2xl font-bold" style={{color: '#2A9FCB'}}>€59<span className="text-sm text-[#2A9FCB]/70">/mese individuale</span></p>
                    <p className="text-lg font-bold" style={{color: '#2A9FCB'}}>€179<span className="text-sm text-[#2A9FCB]/70">/mese famiglia (fino a 5 persone)</span></p>
                  </div>
                  <h4 className="font-semibold text-green-600 mb-2">✅ Cosa include:</h4>
                  <ul className="space-y-1 text-sm mb-4">
                    <li>✓ 2 visite oculistiche/anno</li>
                    <li>✓ Esami diagnostici base (visus, pressione, fundus)</li>
                    <li>✓ 10% sconto su visite extra</li>
                    <li>✓ Prenotazione online</li>
                    <li>✓ Accesso area test online</li>
                    <li>✓ Patient Card digitale</li>
                  </ul>
                  <h4 className="font-semibold text-red-500 mb-2">❌ Non include:</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>✗ OCT, Campo Visivo, Topografia</li>
                    <li>✗ Telemedicina / Videoconsulto</li>
                    <li>✗ Chat diretta con medico</li>
                    <li>✗ Servizio autista</li>
                    <li>✗ Interventi chirurgici</li>
                    <li>✗ Iniezioni intravitreali</li>
                  </ul>
                </div>

                {/* PREMIUM */}
                <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-500 relative">
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">CONSIGLIATO</span>
                  <h3 className="font-bold text-xl mb-2">PREMIUM</h3>
                  <div className="mb-4">
                    <p className="text-2xl font-bold" style={{color: '#2A9FCB'}}>€159<span className="text-sm text-blue-600/70">/mese individuale</span></p>
                    <p className="text-lg font-bold" style={{color: '#2A9FCB'}}>€499<span className="text-sm text-blue-600/70">/mese famiglia (fino a 5 persone)</span></p>
                  </div>
                  <h4 className="font-semibold text-green-600 mb-2">✅ Cosa include:</h4>
                  <ul className="space-y-1 text-sm mb-4">
                    <li>✓ <strong>Visite illimitate</strong></li>
                    <li>✓ Esami diagnostici avanzati (OCT, Campo Visivo, Topografia, Pachimetria)</li>
                    <li>✓ <strong>Telemedicina / Videoconsulto</strong> (Lun-Ven 12:30-13:00)</li>
                    <li>✓ <strong>Chat diretta con il medico</strong></li>
                    <li>✓ <strong>Servizio autista</strong> (presa e riaccompagnamento)</li>
                    <li>✓ Prenotazione prioritaria (entro 24-48h)</li>
                    <li>✓ 1 intervento chirurgico/anno (se indicato)</li>
                    <li>✓ Fino a 3 iniezioni intravitreali/anno</li>
                    <li>✓ Patient Card Premium (blu)</li>
                    <li>✓ Accesso prioritario a tutte le prestazioni</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mb-6">
                <p className="text-sm"><strong>💡 Nota:</strong> Gli abbonamenti possono essere singoli o familiari (fino a 5 persone dello stesso nucleo familiare).</p>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                <h3 className="font-bold text-lg mb-4">Come Funzionano i Guadagni del Medico</h3>
                <div className="space-y-4 text-sm">
                  <p><strong>Tariffa base visita:</strong> €60 a paziente</p>
                  <p><strong>Visite incluse in abbonamento:</strong> Il medico riceve €60 per ogni visita, indipendentemente che sia coperta da abbonamento o pagata singolarmente.</p>
                  <p><strong>Procedure speciali:</strong> Bonus aggiuntivi per OCT, Campo Visivo, Laser, ecc.</p>
                  <p><strong>Monitoraggio:</strong> Le statistiche nel pannello admin mostrano:
                    <ul className="list-disc list-inside ml-4 mt-2">
                      <li>Numero pazienti visitati (ultimo mese, 3 mesi, 6 mesi, anno)</li>
                      <li>Guadagno totale (pazienti × €60)</li>
                      <li>Confronto con periodi precedenti</li>
                    </ul>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MANSIONARI */}
        {activeSection === 'mansionari' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold mb-6" style={{color: '#2A9FCB'}}>
                <FileText className="w-7 h-7 inline mr-3" />
                Mansionari per Ruolo
              </h2>

              <div className="space-y-4">
                {roles.map((role) => (
                  <div key={role.id} className="border rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                      className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: `${role.color}20`}}>
                          <role.icon className="w-6 h-6" style={{color: role.color}} />
                        </div>
                        <div className="text-left">
                          <h3 className="font-bold text-lg">{role.title}</h3>
                          {role.subtitle && <p className="text-sm text-[#2A9FCB]/70">{role.subtitle}</p>}
                        </div>
                      </div>
                      {expandedRole === role.id ? (
                        <ChevronDown className="w-5 h-5 text-[#2A9FCB]/60" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-[#2A9FCB]/60" />
                      )}
                    </button>

                    {expandedRole === role.id && (
                      <div className="p-6 pt-0 space-y-6">
                        {/* Organigramma */}
                        <div className="bg-blue-50 rounded-xl p-4">
                          <h4 className="font-semibold mb-2 text-blue-700">📊 Organigramma</h4>
                          <p className="text-sm"><strong>Riporta a:</strong> {role.reportTo}</p>
                        </div>

                        {/* Descrizione Ruolo */}
                        <div>
                          <h4 className="font-semibold mb-2">📝 Descrizione Ruolo</h4>
                          <p className="text-sm text-[#2A9FCB]/80">{role.description}</p>
                        </div>

                        {/* Responsabilità */}
                        <div>
                          <h4 className="font-semibold mb-3">✅ Responsabilità e Compiti</h4>
                          <div className="space-y-4">
                            {role.responsibilities.map((resp, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-medium mb-2" style={{color: role.color}}>{resp.category}</h5>
                                <ul className="space-y-1">
                                  {resp.tasks.map((task, tidx) => (
                                    <li key={tidx} className="text-sm text-[#2A9FCB]/80 flex items-start gap-2">
                                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background: role.color}}></span>
                                      {task}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* KPI */}
                        <div className="bg-green-50 rounded-xl p-4">
                          <h4 className="font-semibold mb-3 text-green-700">📈 KPI - Come Misuriamo il Tuo Lavoro</h4>
                          <ul className="space-y-2">
                            {role.kpis.map((kpi, idx) => (
                              <li key={idx} className="text-sm flex items-center gap-2">
                                <Target className="w-4 h-4 text-green-600" />
                                {kpi}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Valore Atteso */}
                        <div className="bg-purple-50 rounded-xl p-4">
                          <h4 className="font-semibold mb-2 text-purple-700">🎯 Cosa PRIME VISION Si Aspetta Da Te</h4>
                          <p className="text-sm text-[#2A9FCB]">{role.expectedValue}</p>
                        </div>

                        {/* Guadagni */}
                        <div className="bg-yellow-50 rounded-xl p-4">
                          <h4 className="font-semibold mb-2 text-yellow-700">💰 Compenso</h4>
                          <p className="text-sm text-[#2A9FCB]">{role.earnings}</p>
                        </div>

                        {/* Comunicazione */}
                        <div className="bg-gray-100 rounded-xl p-4">
                          <h4 className="font-semibold mb-2">💬 Come Comunicare</h4>
                          <ul className="text-sm text-[#2A9FCB]/80 space-y-1">
                            <li>• <strong>Urgenze:</strong> Telefono diretto o di persona</li>
                            <li>• <strong>Comunicazioni normali:</strong> Sistema Messaggi Interni del portale</li>
                            <li>• <strong>Richieste scritte:</strong> Email o messaggio interno</li>
                            <li>• <strong>Tempo di risposta atteso:</strong> Entro 24 ore lavorative</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-8 text-sm text-[#2A9FCB]/60">
        <p>PRIME VISION - Manualistica Interna v1.0</p>
        <p>Ultimo aggiornamento: Gennaio 2026</p>
      </div>
    </div>
  );
};

export default Manualistica;
